import { configureStore } from "@reduxjs/toolkit";
import { setupListeners } from "@reduxjs/toolkit/query";
import type { Reducer } from "redux";
import { createLogger } from "redux-logger";
import {
  persistStore,
  persistReducer,
  FLUSH,
  REHYDRATE,
  PAUSE,
  PERSIST,
  PURGE,
  REGISTER,
} from "redux-persist";
import storage from "redux-persist/lib/storage";
import rootReducer from "../reducers";
import { casesClientSearchPersistTransform } from "./casesClientSearchPersistTransform";
import { appApi } from "./api/appApi";
import "./api/groupsApi";
import "./api/reportsApi";
import "./api/notificationsApi";
import "./api/termsApi";

const loggerMiddleware = createLogger();
const isProduction = process.env.NODE_ENV === "production";

const persistConfig = {
  key: "root",
  storage,
  whitelist: ["auth", "role", "permission", "featureAccess", "casesClientSearch"],
  transforms: [casesClientSearchPersistTransform],
};

type RootReducerState = ReturnType<typeof rootReducer>;

const persistedReducer = persistReducer(
  persistConfig,
  rootReducer as Reducer<RootReducerState>
) as Reducer<RootReducerState>;

export const store = configureStore({
  reducer: persistedReducer,
  devTools: !isProduction,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
      },
    })
      .concat(appApi.middleware)
      .concat(isProduction ? [] : [loggerMiddleware]),
});

export const persistor = persistStore(store);
setupListeners(store.dispatch);

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
